<template>
<div class='spider_chart no-select'>
  <svg :height="viewBox" :width="viewBox" :viewBox="`0 0 ${viewBox} ${viewBox}`">
    <defs>
      <line
        v-for="index in sectionsCount"
        :x1="originPoint"
        :y1="originPoint"
        :x2="xLineCoordinate(index)"
        :y2="yLineCoordinate(index)"
        :key='`spider_chart__line-${index}`'
        :id='`spider_chart__line-${index}`'
      />
    </defs>
    <circle
      v-for='index in 3'
      :r='(size/2) - (innerCircleStep*(index-1))'
      :cx='viewBox/2'
      :cy='viewBox/2'
      :key='`spider_chart__circle-${index}`'
      class='spider_chart__circle'
    />
    <g :id='`lines_group-${index}`' v-for="index in sectionsCount" :key='`lines_group__line-${index}`'>
      <use
        :xlink:href="`#spider_chart__line-${index}`"
        stroke-width="10"
        pointer-events="stroke" />
      <use
        :xlink:href="`#spider_chart__line-${index}`"
        class="spider_chart__line"
      />
    </g>
    <g id='label_group' class="spider_chart__label">
      <text
        v-for="(label, index) in labels"
        :key='`label_group__label-${index}`'
        :id='`label_group__label-${index}`'
        :x="xTextCoordinate(index, label)"
        :y="yTextCoordinate(index)"
        v-text="label"
      />
    </g>
    <g id='axis-labels-group'  class="spider_chart__axis-label">
      <text
        v-for="index in 3"
        :key='`axis-labels-group__label-${index}`'
        :id='`axis-labels-group__label-${index}`'
        :x="xAxisTextCoordinate(index)+5"
        :y="yAxisTextCoordinate(index)-10"
        v-text="axisTextLabel(index)"
      />
    </g>
    <path :d='calculateCatmullRomSpline(createCoordinatesSet(seriesA),1)' class='spider_chart__series-a-area' :style="areaAStyle"/>
    <path v-if='seriesB.length > 0' :d='calculateCatmullRomSpline(createCoordinatesSet(seriesB),1)' class='spider_chart__series-b-area'/>
    <g v-if='seriesB.length > 0' id='series-b-group'>
      <g v-for='(value, index) in seriesB'  :key='`series-b-group_content-${index}`'>
        <circle
          :r='6'
          :cx='xPointCoordinate(index,normalizedValue(index, value))'
          :cy='yPointCoordinate(index,normalizedValue(index, value))'
          :key='`series-b-group_point-${index}`'
          class='spider_chart__bpoint'
        />
        <text
          :x="xPointCoordinate(index,normalizedValue(index, value)) + 10"
          :y="yPointCoordinate(index,normalizedValue(index, value)) + 10"
          :key='`series-b-group_point-label-${index}`'
          :id='`series-b-group_point-label-${index}`'
          v-text="normalizedValue(index, value)"
          :class='["spider_chart__bpoint__blabel",`blabel-${index}`]'
        />
      </g>
    </g>
    <g id='series-a-group'>
      <g v-for='(value, index) in seriesA'  :key='`series-a-group_content-${index}`'>
        <circle
          :r='6'
          :cx='xPointCoordinate(index,normalizedValue(index, value))'
          :cy='yPointCoordinate(index,normalizedValue(index, value))'
          :key='`series-a-group_point-${index}`'
          class='spider_chart__point'
          :style="{'--point-a-color':colorA}"
        />
        <text
          :x="xPointCoordinate(index,normalizedValue(index, value)) + 10"
          :y="yPointCoordinate(index,normalizedValue(index, value)) + 10"
          :key='`series-a-group_point-label-${index}`'
          :id='`series-a-group_point-label-${index}`'
          v-text="normalizedValue(index, value)"
          :class='["spider_chart__point__label",`label-${index}`]'
        />
      </g>
    </g>
  </svg>
</div>
</template>

<script>
import { hexToRgba } from '@/utils/colorUtils';
import { advancedRound } from '@/utils/numberUtils';

export default {
  name: 'SpiderChart',
  props: {
    seriesA: {
      type: Array,
      required: true,
      default: () => [],
    },
    seriesB: {
      type: Array,
      required: true,
      default: () => [],
    },
    seriesMax: {
      type: Array,
      required: false,
      default: () => [],
    },
    labels: {
      type: Array,
      required: true,
      default: () => [],
    },
    colorA: {
      type: String,
      required: false,
      default: () => '#B331CF',
    },
    size: {
      type: Number,
      required: false,
      default: () => 400,
    },
    margin: {
      type: Number,
      required: false,
      default: () => 150,
    },
  },
  computed: {
    originPoint() {
      return ((this.size + this.margin) / 2);
    },
    viewBox() {
      return this.size + this.margin;
    },
    innerCircleStep() {
      return (this.size / 2) / 3;
    },
    baseAngle() {
      return this.calculateBaseAngle(this.sectionsCount);
    },
    sectionsCount() {
      return this.labels.length;
    },
    radius() {
      return this.size / 2;
    },
    maxValue() {
      if (this.isNormalisationValuesExist) {
        return 100;
      }
      return Math.max(Math.max(...this.seriesA), Math.max(...this.seriesB));
    },
    isNormalisationValuesExist() {
      return this.seriesMax.length === this.seriesA.length;
    },
    areaAStyle() {
      return {
        '--color': hexToRgba(this.colorA, 0.2),
        '--color-hover': this.colorA,
      };
    },
  },
  methods: {
    /**
     * Calculate the base angle based on amount of sections.
     * @param total: amount of sections
     * @return {number} the angle of one part based on total
     */
    calculateBaseAngle(total) {
      return (Math.PI * 2) / total;
    },
    /**
     * Get the actual angle basing on current angle index
     * @param index current index
     * @param baseAngle th base angle
     * @return {number} the actual angle of the index
     */
    calculateNewAngle(index, baseAngle) {
      return (Math.PI / 2) + Math.PI + (baseAngle * index);
    },
    xLineCoordinate(index) {
      const newAngle = this.calculateNewAngle((index - 1), this.baseAngle);
      return this.calculateXCoordinate(newAngle, this.radius, this.originPoint);
    },
    yLineCoordinate(index) {
      const newAngle = this.calculateNewAngle((index - 1), this.baseAngle);
      return this.calculateYCoordinate(newAngle, this.radius, this.originPoint);
    },
    xTextCoordinate(index, label) {
      const newAngle = this.calculateNewAngle((index), this.baseAngle);
      const correction = (label.length * 7) / 2;
      return this.calculateXCoordinate(newAngle, this.radius + 40, this.originPoint) - correction;
    },
    yTextCoordinate(index) {
      const newAngle = this.calculateNewAngle((index), this.baseAngle);
      return this.calculateYCoordinate(newAngle, this.radius + 40, this.originPoint);
    },
    xAxisTextCoordinate(index) {
      const newAngle = this.calculateNewAngle(0, this.baseAngle);
      return this.calculateXCoordinate(newAngle, (this.size / 2) - (this.innerCircleStep * (index - 1)), this.originPoint);
    },
    yAxisTextCoordinate(index) {
      const newAngle = this.calculateNewAngle(0, this.baseAngle);
      return this.calculateYCoordinate(newAngle, (this.size / 2) - (this.innerCircleStep * (index - 1)), this.originPoint);
    },
    axisTextLabel(index) {
      return Math.round(this.maxValue / index);
    },
    xPointCoordinate(index, value) {
      const newAngle = this.calculateNewAngle((index), this.baseAngle);
      const angleMultiplier = (value / this.maxValue) * this.radius;
      return this.calculateXCoordinate(newAngle, angleMultiplier, this.originPoint);
    },
    yPointCoordinate(index, value) {
      const newAngle = this.calculateNewAngle((index), this.baseAngle);
      const angleMultiplier = (value / this.maxValue) * this.radius;
      return this.calculateYCoordinate(newAngle, angleMultiplier, this.originPoint);
    },
    normalizedValue(index, value) {
      if (!this.isNormalisationValuesExist) {
        return value;
      }
      return advancedRound((100 * value) / this.seriesMax[index]);
    },
    /**
     * Base function to calculate any x coordinate on the given circle
     * @param angle the angle
     * @param angleMultiplier section
     * @param origin circle center
     * @return {*} x coordinate
     */
    calculateXCoordinate(angle, angleMultiplier, origin) {
      return origin + Math.cos(angle) * angleMultiplier;
    },
    /**
     * Base function to calculate any y coordinate on the given circle
     * @param angle the angle
     * @param angleMultiplier section
     * @param origin circle center
     * @return {*} y coordinate
     */
    calculateYCoordinate(angle, angleMultiplier, origin) {
      return (origin + Math.sin(angle) * angleMultiplier);
    },
    createCoordinatesSet(series) {
      const data = [];
      series.forEach((value, index) => {
        const normalizedValue = (value === 0) ? 0.3 : value; // Done to avoid spline intersection
        data.push(this.xPointCoordinate(index, this.normalizedValue(index, normalizedValue)));
        data.push(this.yPointCoordinate(index, this.normalizedValue(index, normalizedValue)));
      });
      return data;
    },
    /**
     * Calculates smooth curve path that pass through set of given coordinates
     * @param coordinates the coordinates
     * @param tension smoothness.
     * @return {string} svg curve
     */
    calculateCatmullRomSpline(coordinates, tension) {
      // eslint-disable-next-line no-param-reassign
      if (tension == null) tension = 1;
      // Duplicating the first 3 point to create closed spline
      coordinates.push(coordinates[0]);
      coordinates.push(coordinates[1]);
      coordinates.push(coordinates[2]);
      coordinates.push(coordinates[3]);
      coordinates.push(coordinates[4]);
      coordinates.push(coordinates[5]);
      const size = coordinates.length;
      const last = size - 4;
      let path = `M${[coordinates[2], coordinates[3]]}`;
      for (let i = 2; i < size - 4; i += 2) {
        const x0 = i ? coordinates[i - 2] : coordinates[0];
        const y0 = i ? coordinates[i - 1] : coordinates[1];
        const x1 = coordinates[i];
        const y1 = coordinates[i + 1];
        const x2 = coordinates[i + 2];
        const y2 = coordinates[i + 3];
        const x3 = i !== last ? coordinates[i + 4] : x2;
        const y3 = i !== last ? coordinates[i + 5] : y2;
        const cp1x = x1 + (x2 - x0) / (6 * tension);
        const cp1y = y1 + (y2 - y0) / (6 * tension);
        const cp2x = x2 - (x3 - x1) / (6 * tension);
        const cp2y = y2 - (y3 - y1) / (6 * tension);
        path += `C${[cp1x, cp1y, cp2x, cp2y, x2, y2]}`;
      }
      return path;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.spider_chart {
  &__circle {
    fill: none;
    stroke: var(--text-muted);
    stroke-width: 1px;
  }

  &__line {
    fill: none;
    stroke: var(--text-muted);
    stroke-width: 2px;
  }

  &__series-a-area {
    fill: var(--color);
    stroke: none;

    &:hover {
      stroke: var(--color-hover);
    }
  }

  &__series-b-area {
    fill: none;
    stroke: var(--severity-low);
    stroke-dasharray: 5;
    stroke-width: 2px;
  }

  &__label {
    fill: var(--text-tertiary);
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    letter-spacing: 1.1px;
    line-height: rem(15);
    text-transform: uppercase;
  }

  &__axis-label {
    fill: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: $font-size-sm;
  }

  &__point {
    fill: var(--point-a-color);
    stroke: white;
    stroke-width: 2px;

    &__label {
      display: none;
      fill: var(--text-primary);
      font-family: $font-family-regular;
      font-size: $font-size-2xs;
      letter-spacing: 0;
      line-height: rem(15);
    }

    &:hover + &__label {
      display: block;
    }
  }

  &__bpoint {
    fill: var(--status-success);
    stroke: white;
    stroke-width: 2px;

    &__blabel {
      display: none;
      fill: var(--text-primary);
      font-family: $font-family-regular;
      font-size: $font-size-2xs;
      letter-spacing: 0;
      line-height: rem(15);
    }

    &:hover + &__blabel {
      display: block;
    }
  }
}

g[id^="lines_group-"]:hover .spider_chart__line {
  stroke: var(--text-primary);
  stroke-dasharray: 5;
}

.no-select {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
</style>
